import { Grid, NumberEasing, Skeleton, Tag, Text } from '@/components'

type ValueUnitPosition = 'prefix' | 'suffix'

interface SummarySideProps {
  label: string
  value: number
  decimals?: number
  unit?: string
  unitPosition?: ValueUnitPosition
  valueColor?: string
}

export interface ThermostatSummaryCardProps {
  period: string
  left: SummarySideProps
  right: SummarySideProps
  tagColor?: string
  tagClassName?: string
  loading?: boolean
}

const DEFAULT_VALUE_COLOR = 'text-themed-grey-900'

function SummaryValue({
  label,
  value,
  decimals = 0,
  unit,
  unitPosition = 'suffix',
  valueColor = DEFAULT_VALUE_COLOR,
  isLoading,
}: SummarySideProps & { isLoading?: boolean }) {
  const valueSkeleton = (
    <div className="flex justify-center">
      <Skeleton
        animation="wave"
        variant="text"
        width={78}
        height={28}
        className="mx-auto"
      />
    </div>
  )

  if (isLoading) {
    return (
      <Grid flow="row">
        <Text variant="body2" className="whitespace-nowrap">
          {label}
        </Text>
        {valueSkeleton}
      </Grid>
    )
  }

  return (
    <Grid flow="row">
      <Text variant="body2" className="whitespace-nowrap">
        {label}
      </Text>
      {unitPosition === 'prefix' ? (
        <Text variant="h1" className={valueColor}>
          {unit}
          <NumberEasing
            value={value}
            decimals={decimals}
            speed={400}
            ease="quintOut"
          />
        </Text>
      ) : (
        <Grid gap="2px" placeItems="baseline" templateColumns="auto 1fr">
          <Text variant="h1" className={valueColor}>
            <NumberEasing
              value={value}
              decimals={decimals}
              speed={400}
              ease="quintOut"
            />
          </Text>
          {unit ? <Text>{unit}</Text> : null}
        </Grid>
      )}
    </Grid>
  )
}

export default function ThermostatSummaryCard(
  props: ThermostatSummaryCardProps,
) {
  return (
    <Tag color={props.tagColor} className={props.tagClassName}>
      <Grid flow="row" gap="16px">
        <Text className="text-left">{props.period}</Text>
        <Grid gap="64px" templateColumns="88px 1fr">
          <SummaryValue
            label={props.left.label}
            value={props.left.value}
            decimals={props.left.decimals}
            unit={props.left.unit}
            unitPosition={props.left.unitPosition}
            valueColor={props.left.valueColor}
            isLoading={props.loading}
          />
          <SummaryValue
            label={props.right.label}
            value={props.right.value}
            decimals={props.right.decimals}
            unit={props.right.unit}
            unitPosition={props.right.unitPosition}
            valueColor={props.right.valueColor}
            isLoading={props.loading}
          />
        </Grid>
      </Grid>
    </Tag>
  )
}
